import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { api } from "../api";
import { getCurrentUser, logout as apiLogout } from "../services/auth";

// Єдине джерело правди про поточного користувача та його воркспейси.
// Замінює розкидані виклики getCurrentUser() по сторінках.
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const u = await getCurrentUser();
    setUser(u);
    if (u) {
      try {
        const res = await api.getMyWorkspaces();
        setWorkspaces(res.workspaces || []);
        setActiveWorkspaceId(res.activeWorkspaceId ?? null);
      } catch {
        setWorkspaces([]);
        setActiveWorkspaceId(null);
      }
    } else {
      setWorkspaces([]);
      setActiveWorkspaceId(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Зробити воркспейс активним: бек переоформлює cookie, далі перечитуємо
  // профіль і членства (роль у токені могла змінитись).
  const switchWorkspace = useCallback(
    async (id) => {
      await api.switchWorkspace(id);
      await load();
    },
    [load]
  );

  const logout = useCallback(async () => {
    await apiLogout();
    localStorage.removeItem("user");
    setUser(null);
    setWorkspaces([]);
    setActiveWorkspaceId(null);
  }, []);

  const activeWorkspace =
    workspaces.find((w) => w.id === activeWorkspaceId) || null;
  const workspaceRole = activeWorkspace?.role || null;

  const value = {
    user,
    loading,
    workspaces,
    activeWorkspaceId,
    activeWorkspace,
    workspaceRole,
    refresh: load,
    setUser,
    switchWorkspace,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
